import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, UploadCloud, X, Loader2, Image as ImageIcon } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const CATS = ["Particuliers", "Entreprises", "Collectivités"];
const empty = {
  title: "", category: "Particuliers", location: "", summary: "", cover_image: "", gallery: [],
  challenge: "", solution: "", results: "", services_used: [], surface: "", duration: "", year: "",
  tasks: [], interviews: [], video_gallery: [],
  published: true, seo_title: "", seo_description: "",
};

function UploadZone({ onUploaded, multiple }) {
  const [busy, setBusy] = useState(false);
  const handle = async (files) => {
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded(data.path);
      }
      toast.success("Image téléversée");
    } catch { toast.error("Échec du téléversement"); }
    finally { setBusy(false); }
  };
  return (
    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-[#F0EFEA] py-6 cursor-pointer hover:border-brand transition-colors" data-testid="upload-zone">
      {busy ? <Loader2 className="animate-spin text-brand" /> : <UploadCloud className="text-brand" />}
      <span className="text-sm text-stone">{busy ? "Envoi…" : "Cliquez pour téléverser une image"}</span>
      <input type="file" accept="image/*" multiple={multiple} className="hidden" onChange={(e) => e.target.files.length && handle(e.target.files)} />
    </label>
  );
}

export default function RealisationsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/realisations", { params: { published_only: false } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...empty, ...r }); setEditing(r.id); };

  const save = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const toArr = (v) => Array.isArray(v) ? v : String(v || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        services_used: Array.isArray(form.services_used) ? form.services_used : String(form.services_used).split(",").map((s) => s.trim()).filter(Boolean),
        tasks: toArr(form.tasks).filter(Boolean),
        video_gallery: toArr(form.video_gallery).filter(Boolean),
        interviews: (form.interviews || []).filter((v) => v && (v.url || v.name)),
      };
      if (editing === "new") await api.post("/realisations", payload);
      else await api.put(`/realisations/${editing}`, payload);
      toast.success("Enregistré");
      setEditing(null); load();
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Supprimer cette réalisation ?")) return; await api.delete(`/realisations/${id}`); load(); };

  const updateInterview = (i, key, val) => setForm((f) => { const arr = [...(f.interviews || [])]; arr[i] = { ...arr[i], [key]: val }; return { ...f, interviews: arr }; });

  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display text-4xl text-forest">Réalisations</h1><p className="text-stone">Gérez vos études de cas</p></div>
        <button onClick={openNew} data-testid="add-realisation" className="inline-flex items-center gap-2 rounded-full bg-brand text-cream px-5 py-2.5 text-sm font-semibold hover:bg-forest transition-colors"><Plus size={17} /> Ajouter</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl bg-white border border-border overflow-hidden" data-testid={`admin-realisation-${r.id}`}>
            <div className="aspect-video bg-sage flex items-center justify-center overflow-hidden">
              {r.cover_image ? <img src={fileUrl(r.cover_image)} alt={r.title} className="h-full w-full object-cover" /> : <ImageIcon className="text-stone/40" size={32} />}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-terracotta">{r.category}</span>
                {!r.published && <span className="text-[10px] bg-stone/20 text-stone px-2 py-0.5 rounded">Brouillon</span>}
              </div>
              <h3 className="font-display text-xl text-forest mt-1">{r.title}</h3>
              <p className="text-xs text-stone">{r.location}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} className="flex-1 rounded-lg bg-sage text-forest text-sm py-1.5 flex items-center justify-center gap-1 hover:bg-brand hover:text-cream transition-colors"><Pencil size={14} /> Modifier</button>
                <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="rounded-lg bg-red-50 text-red-600 px-3 hover:bg-red-100"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-stone col-span-full py-10 text-center">Aucune réalisation. Cliquez sur « Ajouter ».</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" data-testid="realisation-modal">
            <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="font-display text-2xl text-forest">{editing === "new" ? "Nouvelle réalisation" : "Modifier"}</h2>
              <button onClick={() => setEditing(null)} data-testid="close-modal"><X className="text-stone" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-stone">Titre *</label><input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="f-title" /></div>
                <div><label className="text-xs text-stone">Catégorie</label><select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="f-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className="text-xs text-stone">Lieu</label><input className={input} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div><label className="text-xs text-stone">Surface</label><input className={input} value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} /></div>
                <div><label className="text-xs text-stone">Durée</label><input className={input} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-stone">Année</label><input className={input} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
                <div><label className="text-xs text-stone">Prestations (virgules)</label><input className={input} value={Array.isArray(form.services_used) ? form.services_used.join(", ") : form.services_used} onChange={(e) => setForm({ ...form, services_used: e.target.value })} /></div>
              </div>
              <div><label className="text-xs text-stone">Résumé</label><textarea rows={2} className={input} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>

              <div><label className="text-xs text-stone">Image de couverture</label>
                {form.cover_image && <img src={fileUrl(form.cover_image)} className="mt-2 h-28 rounded-lg object-cover" alt="cover" />}
                <div className="mt-2"><UploadZone onUploaded={(p) => setForm((f) => ({ ...f, cover_image: p }))} /></div>
              </div>

              <div><label className="text-xs text-stone">Galerie</label>
                {form.gallery?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.gallery.map((g, i) => (
                      <div key={i} className="relative"><img src={fileUrl(g)} className="h-16 w-16 rounded object-cover" alt="" />
                        <button onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2"><UploadZone multiple onUploaded={(p) => setForm((f) => ({ ...f, gallery: [...(f.gallery || []), p] }))} /></div>
              </div>

              <p className="font-semibold text-forest text-sm pt-2">Chantier — tâches & vidéos</p>
              <div><label className="text-xs text-stone">Tâches réalisées (une par ligne)</label>
                <textarea rows={4} className={input} placeholder={"Terrassement et nivellement\nPose d'un système d'arrosage\nPlantation de 40 végétaux méditerranéens"} value={(form.tasks || []).join("\n")} onChange={(e) => setForm({ ...form, tasks: e.target.value.split("\n") })} data-testid="f-tasks" />
              </div>

              <div><label className="text-xs text-stone">Interviews vidéo (client, architecte, partenaires…)</label>
                <div className="mt-2 space-y-3">
                  {(form.interviews || []).map((v, i) => (
                    <div key={i} className="grid sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center rounded-lg border border-border p-2 bg-[#F9F8F5]">
                      <input className={input} placeholder="Rôle (Client, Architecte…)" value={v.role || ""} onChange={(e) => updateInterview(i, "role", e.target.value)} />
                      <input className={input} placeholder="Nom" value={v.name || ""} onChange={(e) => updateInterview(i, "name", e.target.value)} />
                      <input className={input} placeholder="Lien YouTube / Vimeo" value={v.url || ""} onChange={(e) => updateInterview(i, "url", e.target.value)} data-testid={`f-interview-url-${i}`} />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, interviews: f.interviews.filter((_, j) => j !== i) }))} className="rounded-lg bg-red-50 text-red-600 h-9 w-9 flex items-center justify-center hover:bg-red-100"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setForm((f) => ({ ...f, interviews: [...(f.interviews || []), { role: "", name: "", url: "" }] }))} data-testid="add-interview" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sage text-forest px-4 py-1.5 text-sm font-medium hover:bg-brand hover:text-cream transition-colors"><Plus size={14} /> Ajouter une interview</button>
              </div>

              <div><label className="text-xs text-stone">Galerie vidéo du projet (un lien YouTube/Vimeo par ligne)</label>
                <textarea rows={3} className={input} placeholder={"https://youtu.be/xxxx\nhttps://vimeo.com/xxxx"} value={(form.video_gallery || []).join("\n")} onChange={(e) => setForm({ ...form, video_gallery: e.target.value.split("\n") })} data-testid="f-video-gallery" />
              </div>

              <p className="font-semibold text-forest text-sm pt-2">Étude de cas</p>
              <div><label className="text-xs text-stone">Le défi</label><textarea rows={2} className={input} value={form.challenge} onChange={(e) => setForm({ ...form, challenge: e.target.value })} /></div>
              <div><label className="text-xs text-stone">Notre solution</label><textarea rows={2} className={input} value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} /></div>
              <div><label className="text-xs text-stone">Les résultats</label><textarea rows={2} className={input} value={form.results} onChange={(e) => setForm({ ...form, results: e.target.value })} /></div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-stone">SEO Titre</label><input className={input} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></div>
                <div><label className="text-xs text-stone">SEO Description</label><input className={input} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-forest"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="f-published" /> Publié sur le site</label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 text-sm text-stone hover:bg-sage">Annuler</button>
              <button onClick={save} disabled={saving} data-testid="save-realisation" className="rounded-full bg-brand text-cream px-6 py-2.5 text-sm font-semibold hover:bg-forest flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
